import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';

export interface SearchableField {
  fieldName: string;
  fieldType: string;
  description: string;
  standardId: string;
  standardSlug: string;
  standardTitle: string;
  isRequired: boolean;
  format?: string;
  enumValues?: string[];
  usedInStandards: { slug: string; title: string }[];
}

export interface SearchableStandard {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  categoryName: string;
}

export interface SearchableEnumValue {
  value: string;
  displayName: string;
  lookupGroup: string;
  description?: string;
  usedInFields: { fieldName: string; standardTitle: string; standardSlug: string }[];
}

export interface SearchableDataType {
  id: string;
  slug: string;
  name: string;
  category: string;
  shortDescription: string;
  exampleValue?: string;
}

export interface SearchableGuide {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  categoryName: string;
}

export interface SearchResult {
  type: 'standard' | 'field' | 'enum' | 'dataType' | 'guide';
  standard?: SearchableStandard;
  field?: SearchableField;
  enumValue?: SearchableEnumValue;
  dataType?: SearchableDataType;
  guide?: SearchableGuide;
  score: number;
}

interface FieldMappingWithLibrary {
  id: string;
  is_required: boolean;
  lookup_group: string | null;
  inline_enum: Json | null;
  field_library: {
    slug: string;
    name: string;
    description: string | null;
    data_type: string;
    format: string | null;
  };
}

interface DatabaseStandard {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string;
  category_name: string;
  standard_field_mappings: FieldMappingWithLibrary[];
}

interface DatabaseLookup {
  id: string;
  value: string;
  display_name: string;
  lookup_group: string;
  description: string | null;
}

interface DatabaseDataType {
  id: string;
  slug: string;
  name: string;
  category: string;
  short_description: string | null;
  example_value: string | null;
}

interface DatabaseGuide {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  doc_categories: { name: string } | null;
}

export function useSearchIndex() {
  const [standards, setStandards] = useState<SearchableStandard[]>([]);
  const [fields, setFields] = useState<SearchableField[]>([]);
  const [enumValues, setEnumValues] = useState<SearchableEnumValue[]>([]);
  const [dataTypes, setDataTypes] = useState<SearchableDataType[]>([]);
  const [guides, setGuides] = useState<SearchableGuide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setError(null);
        
        const [standardsRes, lookupsRes, dataTypesRes, guidesRes] = await Promise.all([
          supabase
            .from('data_standards')
            .select(`
              id, slug, title, description, category, category_name,
              standard_field_mappings (
                id,
                is_required,
                lookup_group,
                inline_enum,
                field_library (
                  slug,
                  name,
                  description,
                  data_type,
                  format
                )
              )
            `)
            .eq('is_active', true),
          supabase
            .from('lookups')
            .select('id, value, display_name, lookup_group, description')
            .eq('is_active', true),
          supabase
            .from('data_type_docs')
            .select('id, slug, name, category, short_description, example_value')
            .eq('is_active', true),
          supabase
            .from('documentation')
            .select('id, slug, title, excerpt, doc_categories(name)')
            .eq('is_active', true),
        ]);

        if (standardsRes.error) throw standardsRes.error;
        if (lookupsRes.error) throw lookupsRes.error;
        if (dataTypesRes.error) throw dataTypesRes.error;
        if (guidesRes.error) throw guidesRes.error;

        const standardsData = (standardsRes.data || []) as unknown as DatabaseStandard[];
        const lookupsData = lookupsRes.data as DatabaseLookup[] || [];
        const dataTypesData = dataTypesRes.data as DatabaseDataType[] || [];
        const guidesData = guidesRes.data as DatabaseGuide[] || [];

        // Group lookups by group for enum resolution
        const lookupsByGroup: Record<string, string[]> = {};
        lookupsData.forEach(l => {
          if (!lookupsByGroup[l.lookup_group]) {
            lookupsByGroup[l.lookup_group] = [];
          }
          lookupsByGroup[l.lookup_group].push(l.value);
        });

        // Build standards index
        const searchableStandards: SearchableStandard[] = standardsData.map(s => ({
          id: s.id,
          slug: s.slug,
          title: s.title,
          description: s.description || '',
          category: s.category,
          categoryName: s.category_name,
        }));
        setStandards(searchableStandards);

        // Build field cross-reference map
        const fieldToStandards = new Map<string, { slug: string; title: string }[]>();
        const allFields: SearchableField[] = [];

        standardsData.forEach(standard => {
          const mappings = standard.standard_field_mappings || [];
          mappings.forEach((mapping) => {
            if (!mapping.field_library) return;
            const fieldKey = mapping.field_library.slug.toLowerCase();
            
            if (!fieldToStandards.has(fieldKey)) {
              fieldToStandards.set(fieldKey, []);
            }
            fieldToStandards.get(fieldKey)!.push({
              slug: standard.slug,
              title: standard.title,
            });
          });
        });

        // Build searchable fields with cross-references
        standardsData.forEach(standard => {
          const mappings = standard.standard_field_mappings || [];
          mappings.forEach((mapping) => {
            if (!mapping.field_library) return;
            const usedIn = fieldToStandards.get(mapping.field_library.slug.toLowerCase()) || [];
            
            // Resolve enum values from inline_enum or lookup_group
            let enumVals: string[] | undefined;
            if (mapping.inline_enum && Array.isArray(mapping.inline_enum)) {
              enumVals = mapping.inline_enum as string[];
            } else if (mapping.lookup_group && lookupsByGroup[mapping.lookup_group]) {
              enumVals = lookupsByGroup[mapping.lookup_group];
            }
            
            allFields.push({
              fieldName: mapping.field_library.slug,
              fieldType: mapping.field_library.data_type,
              description: mapping.field_library.description || '',
              standardId: standard.id,
              standardSlug: standard.slug,
              standardTitle: standard.title,
              isRequired: mapping.is_required,
              format: mapping.field_library.format || undefined,
              enumValues: enumVals,
              usedInStandards: usedIn.filter(s => s.slug !== standard.slug),
            });
          });
        });
        setFields(allFields);

        // Build enum values index with field references
        const enumToFields = new Map<string, { fieldName: string; standardTitle: string; standardSlug: string }[]>();
        
        standardsData.forEach(standard => {
          const mappings = standard.standard_field_mappings || [];
          mappings.forEach((mapping) => {
            if (!mapping.field_library) return;
            
            // Check inline_enum
            const inlineEnums = mapping.inline_enum as string[] | null;
            if (inlineEnums && Array.isArray(inlineEnums)) {
              inlineEnums.forEach(enumVal => {
                const key = enumVal.toLowerCase();
                if (!enumToFields.has(key)) {
                  enumToFields.set(key, []);
                }
                enumToFields.get(key)!.push({
                  fieldName: mapping.field_library.slug,
                  standardTitle: standard.title,
                  standardSlug: standard.slug,
                });
              });
            }
            
            // Check lookup_group
            if (mapping.lookup_group && lookupsByGroup[mapping.lookup_group]) {
              lookupsByGroup[mapping.lookup_group].forEach(enumVal => {
                const key = enumVal.toLowerCase();
                if (!enumToFields.has(key)) {
                  enumToFields.set(key, []);
                }
                enumToFields.get(key)!.push({
                  fieldName: mapping.field_library.slug,
                  standardTitle: standard.title,
                  standardSlug: standard.slug,
                });
              });
            }
          });
        });

        const searchableEnums: SearchableEnumValue[] = lookupsData.map(l => ({
          value: l.value,
          displayName: l.display_name,
          lookupGroup: l.lookup_group,
          description: l.description || undefined,
          usedInFields: enumToFields.get(l.value.toLowerCase()) || [],
        }));
        setEnumValues(searchableEnums);

        // Build data types index
        const searchableDataTypes: SearchableDataType[] = dataTypesData.map(d => ({
          id: d.id,
          slug: d.slug,
          name: d.name,
          category: d.category,
          shortDescription: d.short_description || '',
          exampleValue: d.example_value || undefined,
        }));
        setDataTypes(searchableDataTypes);

        // Build guides index
        const searchableGuides: SearchableGuide[] = guidesData.map(g => ({
          id: g.id,
          slug: g.slug,
          title: g.title,
          excerpt: g.excerpt || '',
          categoryName: g.doc_categories?.name || 'Guides',
        }));
        setGuides(searchableGuides);

      } catch (err) {
        console.error('Error building search index:', err);
        setError(err instanceof Error ? err.message : 'Failed to build search index');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, []);

  const scoreMatch = useCallback((query: string, text: string): number => {
    const q = query.toLowerCase();
    const t = text.toLowerCase();
    
    if (t === q) return 100;
    if (t.startsWith(q)) return 80;
    if (t.includes(q)) return 60;
    
    let qIdx = 0;
    for (let i = 0; i < t.length && qIdx < q.length; i++) {
      if (t[i] === q[qIdx]) qIdx++;
    }
    if (qIdx === q.length) return 40;
    
    return 0;
  }, []);

  const searchAll = useCallback((query: string): SearchResult[] => {
    if (!query || query.length < 2) return [];
    
    const results: SearchResult[] = [];
    
    standards.forEach(s => {
      const titleScore = scoreMatch(query, s.title);
      const descScore = scoreMatch(query, s.description) * 0.5;
      const score = Math.max(titleScore, descScore);
      if (score > 0) {
        results.push({ type: 'standard', standard: s, score });
      }
    });
    
    fields.forEach(f => {
      const nameScore = scoreMatch(query, f.fieldName);
      const descScore = scoreMatch(query, f.description) * 0.5;
      const score = Math.max(nameScore, descScore);
      if (score > 0) {
        results.push({ type: 'field', field: f, score });
      }
    });
    
    enumValues.forEach(e => {
      const valueScore = scoreMatch(query, e.value);
      const displayScore = scoreMatch(query, e.displayName);
      const score = Math.max(valueScore, displayScore);
      if (score > 0) {
        results.push({ type: 'enum', enumValue: e, score });
      }
    });
    
    dataTypes.forEach(d => {
      const nameScore = scoreMatch(query, d.name);
      const descScore = scoreMatch(query, d.shortDescription) * 0.5;
      const score = Math.max(nameScore, descScore);
      if (score > 0) {
        results.push({ type: 'dataType', dataType: d, score });
      }
    });

    guides.forEach(g => {
      const titleScore = scoreMatch(query, g.title);
      const excerptScore = scoreMatch(query, g.excerpt) * 0.5;
      const score = Math.max(titleScore, excerptScore);
      if (score > 0) {
        results.push({ type: 'guide', guide: g, score });
      }
    });
    
    return results.sort((a, b) => b.score - a.score);
  }, [standards, fields, enumValues, dataTypes, guides, scoreMatch]);

  const searchFields = useCallback((query: string): SearchableField[] => {
    if (!query || query.length < 2) return [];
    
    return fields
      .map(f => ({
        field: f,
        score: Math.max(
          scoreMatch(query, f.fieldName),
          scoreMatch(query, f.description) * 0.5
        ),
      }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(r => r.field);
  }, [fields, scoreMatch]);

  const searchEnumValues = useCallback((query: string): SearchableEnumValue[] => {
    if (!query || query.length < 2) return [];
    
    return enumValues
      .map(e => ({
        enumValue: e,
        score: Math.max(
          scoreMatch(query, e.value),
          scoreMatch(query, e.displayName)
        ),
      }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(r => r.enumValue);
  }, [enumValues, scoreMatch]);

  const searchGrouped = useCallback((query: string) => {
    const all = searchAll(query);
    
    return {
      standards: all.filter(r => r.type === 'standard').slice(0, 5),
      fields: all.filter(r => r.type === 'field').slice(0, 10),
      enums: all.filter(r => r.type === 'enum').slice(0, 5),
      dataTypes: all.filter(r => r.type === 'dataType').slice(0, 3),
      guides: all.filter(r => r.type === 'guide').slice(0, 5),
    };
  }, [searchAll]);

  return {
    isLoading,
    error,
    searchAll,
    searchFields,
    searchEnumValues,
    searchGrouped,
    totalIndexed: useMemo(() => ({
      standards: standards.length,
      fields: fields.length,
      enums: enumValues.length,
      dataTypes: dataTypes.length,
      guides: guides.length,
    }), [standards, fields, enumValues, dataTypes, guides]),
  };
}
